import React, { useRef, useEffect } from "react";
import {
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  Box,
  Autocomplete,
  Chip,
  Typography,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
// import { TimePicker } from "@mui/x-date-pickers";

const DynamicForm = ({
  formData,
  formState,
  handleChange,
  debouncedFilterData,
  filterData,
  error,
  helperText,
  disabled,
}) => {
  const fetchedFieldsRef = useRef(new Set());
  useEffect(() => {
    fetchedFieldsRef.current.clear();
  }, []);

  return (
    <Box
      component="form"
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        borderRadius: 2,
      }}
    >
      {formData?.map((field, index) => {
        if (
          field.name === "endDate" &&
          formData[index - 1]?.name === "startDate"
          //   ||
          // (field.name === "endTime" &&
          //   formData[index - 1]?.name === "startTime")
        ) {
          return null;
        }

        if (
          field.name === "startDate" &&
          formData[index + 1]?.name === "endDate"
        ) {
          const startDate = formState[field.name];
          const endDate = formState[formData[index + 1].name];

          const isEndDateInvalid =
            startDate && endDate && endDate.isBefore(startDate);

          return (
            <Box key="date-range">
              <Typography variant="body1" sx={{ mb: 1 }}>
                Date Range
              </Typography>
              <Box sx={{ display: "flex", gap: 2 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label={field.label}
                    disableFuture
                    value={startDate || null}
                    onChange={(newValue) => handleChange(field.name, newValue)}
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                        clearable: true,
                      },
                    }}
                  />
                </LocalizationProvider>

                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label={formData[index + 1].label}
                    disableFuture
                    value={endDate || null}
                    onChange={(newValue) =>
                      handleChange(formData[index + 1].name, newValue)
                    }
                    minDate={startDate || undefined}
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                        clearable: true,
                        error: isEndDateInvalid,
                        helperText: isEndDateInvalid
                          ? "End date must be after Start date"
                          : "",
                      },
                    }}
                  />
                </LocalizationProvider>
              </Box>
            </Box>
          );
        }

        // if (
        //   field.name === "startTime" &&
        //   formData[index + 1]?.name === "endTime"
        // ) {
        //   const isStartDateEmpty = !formState["startDate"];

        //   return (
        //     <Box key="time-range">
        //       <Typography variant="body1" sx={{ mb: 1 }}>
        //         Time Range
        //       </Typography>
        //       <Box sx={{ display: "flex", gap: 2 }}>
        //         <LocalizationProvider dateAdapter={AdapterDayjs}>
        //           <TimePicker
        //             label={field.label}
        //             value={formState[field.name] || null}
        //             onChange={(newValue) => handleChange(field.name, newValue)}
        //             disabled={isStartDateEmpty}
        //             slotProps={{
        //               textField: {
        //                 size: "small",
        //                 fullWidth: true,
        //               },
        //             }}
        //           />
        //         </LocalizationProvider>
        //         <LocalizationProvider dateAdapter={AdapterDayjs}>
        //           <TimePicker
        //             label={formData[index + 1].label}
        //             value={formState[formData[index + 1].name] || null}
        //             onChange={(newValue) =>
        //               handleChange(formData[index + 1].name, newValue)
        //             }
        //             disabled={isStartDateEmpty}
        //             slotProps={{
        //               textField: {
        //                 size: "small",
        //                 fullWidth: true,
        //               },
        //             }}
        //           />
        //         </LocalizationProvider>
        //       </Box>
        //     </Box>
        //   );
        // }

        switch (field?.type) {
          case "text":
            return (
              <TextField
                key={index}
                label={field?.label}
                name={field?.name}
                placeholder={field?.placeholder || ""}
                variant="outlined"
                fullWidth
                value={formState[field?.name] || ""}
                onChange={(e) => handleChange(field?.name, e.target.value)}
              />
            );

          case "select":
            return (
              <FormControl key={index} fullWidth>
                <InputLabel>{field?.label}</InputLabel>
                <Select
                  label={field?.label}
                  value={formState[field?.name] || ""}
                  onChange={(e) => handleChange(field?.name, e.target.value)}
                  name={field?.name}
                >
                  {field?.options?.map((option, idx) => (
                    <MenuItem key={idx} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            );

          case "radio":
            return (
              <FormControl key={index}>
                <Typography variant="body1" sx={{ marginBottom: 1 }}>
                  {field?.label}
                </Typography>
                <RadioGroup
                  name={field?.name}
                  value={formState[field?.name] || ""}
                  onChange={(e) => handleChange(field?.name, e.target.value)}
                >
                  {field?.options?.map((option, idx) => (
                    <FormControlLabel
                      key={idx}
                      value={option.value}
                      control={<Radio />}
                      label={option.label}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            );

          case "checkbox":
            return (
              <FormControl key={index} component="fieldset">
                <Typography variant="body1" sx={{ marginBottom: 1 }}>
                  {field?.label}
                </Typography>
                {field?.options?.map((option, idx) => (
                  <FormControlLabel
                    key={idx}
                    control={
                      <Checkbox
                        name={field?.name}
                        value={option?.value}
                        checked={
                          formState[field?.name]?.includes(option?.value) ||
                          false
                        }
                        onChange={(e) => {
                          const newValue = formState[field?.name] || [];
                          if (e.target.checked) {
                            handleChange(field?.name, [
                              ...newValue,
                              e.target.value,
                            ]);
                          } else {
                            handleChange(
                              field?.name,
                              newValue?.filter((val) => val !== e.target.value)
                            );
                          }
                        }}
                      />
                    }
                    label={option?.label}
                  />
                ))}
              </FormControl>
            );

          case "multi-select":
            return (
              <FormControl key={index} fullWidth>
                <InputLabel>{field?.label}</InputLabel>
                <Select
                  multiple
                  value={formState[field?.name] || []}
                  onChange={(e) => handleChange(field?.name, e.target.value)}
                  name={field?.name}
                  label={field?.label}
                  renderValue={(selected) =>
                    selected
                      .map(
                        (selectedValue) =>
                          field?.options?.find(
                            (option) => option?.value === selectedValue
                          )?.label
                      )
                      .join(", ")
                  }
                >
                  {field?.options?.map((option, idx) => (
                    <MenuItem key={idx} value={option?.value}>
                      <Checkbox
                        checked={
                          formState[field?.name]?.includes(option?.value) ||
                          false
                        }
                      />
                      {option?.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            );

          case "autocomplete-multiple":
            return (
              <Box key={index}>
                <Typography variant="body1" sx={{ mb: 0.5 }}>
                  {field?.text}
                </Typography>
                <Autocomplete
                  size="small"
                  multiple
                  noOptionsText="No results found"
                  options={field?.options || []}
                  getOptionLabel={(option) => option?.label || ""}
                  value={(formState[field?.name] || [])
                    .map((val) =>
                      field?.options?.find((option) => option?.value === val)
                    )
                    .filter(Boolean)}
                  inputValue={formState[`${field?.name}Input`] || ""}
                  onChange={(e, newValue) =>
                    handleChange(
                      field.name,
                      (newValue || []).map((item) => item?.value)
                    )
                  }
                  onInputChange={(e, newInputValue, reason) => {
                    if (reason !== "reset") {
                      handleChange(
                        field.name,
                        formState[field.name],
                        newInputValue
                      );
                    }
                  }}
                  onFocus={() => {
                    if (
                      field.name !== "status" &&
                      filterData &&
                      !fetchedFieldsRef.current.has(field.name) &&
                      (!field.options || field.options.length === 0)
                    ) {
                      filterData({ [`${field.name}Search`]: "" });
                      fetchedFieldsRef.current.add(field.name);
                    }
                  }}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        key={option?.value || index}
                        label={option?.label}
                        {...getTagProps({ index })}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField {...params} label={field?.label} fullWidth />
                  )}
                />
              </Box>
            );

          case "autocomplete-single":
            return (
              <Box key={index}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  {field?.text}
                </Typography>
                <Autocomplete
                  size="small"
                  key={index}
                  options={field?.options || []}
                  disabled={disabled}
                  getOptionLabel={(option) => option?.label}
                  value={
                    field?.options?.find(
                      (option) => option?.value === formState[field?.name]
                    ) || null
                  }
                  onChange={(e, newValue) =>
                    handleChange(field?.name, newValue ? newValue?.value : "")
                  }
                  onInputChange={(e, newInputValue) => {
                    if (debouncedFilterData) {
                      debouncedFilterData({
                        [`${field?.name}Search`]: newInputValue,
                      });
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={field?.label}
                      error={error}
                      helperText={helperText}
                      fullWidth
                    />
                  )}
                />
              </Box>
            );

          default:
            return null;
        }
      })}
    </Box>
  );
};

export default DynamicForm;
